// Rank-assignment (Sugiyama-style layering, no crossing-minimization — v1 scope)
// for UmlWorkspace Auto Layout. Implements rule 1 from rk=d69c50b644e7497cb4d654d3c82f15ac
// ("pijlen altijd naar beneden") — see implementation-detail note
// rk=f114ae65b0584e8e830998f6ad456911 for the pipeline design this belongs to.
//
// Connector edges point dependent -> dependency (fromShapeId depends on/inherits
// from toShapeId, see AssociationConnectorBuilder/InheritanceConnectorBuilder).
// "Arrows always point down" means fromShapeId sits ABOVE toShapeId, so
// rank(to) must be >= rank(from) + 1 — a forward longest-path-from-sources
// layering (Kahn's algorithm), propagated along the edge direction.
//
// Ranking operates on TOP-LEVEL shapes only (umlCanvasEngine.shapes — packages
// + standalone classes). Nested shapes (classes inside a package) are resolved
// to their top-level ancestor first, so a connector between two classes in
// different packages becomes an edge between those packages.

/**
 * Maps every shape id (top-level AND nested) to its top-level ancestor id.
 * One level of nesting only (package -> class), matching the current data model.
 * @param {object[]} shapes - top-level shapes (umlCanvasEngine.shapes)
 * @returns {Map<string, string>}
 */
function buildTopLevelIndex(shapes) {
    const index = new Map();
    shapes.forEach(function indexShape(shape) {
        index.set(shape.id, shape.id);
        (shape.children || []).forEach(function indexChild(child) {
            index.set(child.id, shape.id);
        });
    });
    return index;
}

/**
 * Assigns a rank (Y-layer) per top-level shape id.
 * @param {object[]} shapes - top-level shapes (umlCanvasEngine.shapes)
 * @param {object[]} connectors - all connectors (umlConnectorEngine.connectors)
 * @returns {{ranksById: Map<string, number>, warnings: string[]}}
 */
export function assignRanks(shapes, connectors) {
    const topIndex = buildTopLevelIndex(shapes);
    const topIds = shapes.map(function getId(shape) {
        return shape.id;
    });

    const outEdges = new Map(topIds.map(function initOut(id) { return [id, new Set()]; }));
    const inDegree = new Map(topIds.map(function initIn(id) { return [id, 0]; }));

    connectors.forEach(function addEdge(connector) {
        const from = topIndex.get(connector.fromShapeId);
        const to = topIndex.get(connector.toShapeId);
        if (!from || !to || from === to) return; // unresolvable shape, or same-container edge — no rank implication
        if (!outEdges.get(from).has(to)) {
            outEdges.get(from).add(to);
            inDegree.set(to, inDegree.get(to) + 1);
        }
    });

    const rank = new Map();
    const queue = [];
    topIds.forEach(function seedRoots(id) {
        if (inDegree.get(id) === 0) {
            rank.set(id, 0);
            queue.push(id);
        }
    });

    let processed = 0;
    while (queue.length > 0) {
        const current = queue.shift();
        processed += 1;
        outEdges.get(current).forEach(function propagate(target) {
            const candidateRank = rank.get(current) + 1;
            if (!rank.has(target) || candidateRank > rank.get(target)) {
                rank.set(target, candidateRank);
            }
            inDegree.set(target, inDegree.get(target) - 1);
            if (inDegree.get(target) === 0) {
                queue.push(target);
            }
        });
    }

    const warnings = [];
    if (processed < topIds.length) {
        // Leftover nodes never reached in-degree 0 — part of a cycle. v1: no
        // cycle-resolution, just place them on the current max rank and warn.
        const knownRanks = [...rank.values()];
        const maxRank = knownRanks.length > 0 ? Math.max(...knownRanks) : 0;
        const cycleIds = topIds.filter(function notRanked(id) { return !rank.has(id); });
        cycleIds.forEach(function placeOnMaxRank(id) { rank.set(id, maxRank); });
        warnings.push(`cycle detected, placed on shared rank: ${cycleIds.join(', ')}`);
    }

    return { ranksById: rank, warnings };
}
